import { isString } from 'lodash';

import {
  CurrencyEnum,
  LinkedAccountStatusEnum,
  PaymentMethodEnum,
  PaymentProcessEnum,
  PaymentStatusEnum,
  TimeInForceEnum,
} from '../enums';
import { OrderTypeEnum } from '../enums/order-type.enum';
import { AdjustmentCategoryEnum } from '../enums/payments/adjustment-category.enum';
import lang from '../language';
import { FxOrder, FxOrderStatus } from '../models/reporting/types';

import { isBuyEUR, isSellEUR } from './hooks/wallet/helpers';
import { AllowedFileExtensions, NullableString } from './types';

export class TextHelpers {
  static getLinkedAccountStatusName(status: number) {
    switch (status) {
      case LinkedAccountStatusEnum.New:
        return lang.commonOrderStatusPending();
      case LinkedAccountStatusEnum.Active:
        return lang.fundScreenActiveStatus();
      case LinkedAccountStatusEnum.Inactive:
        return lang.fundScreenInActiveStatus();
      default:
        return '';
    }
  }

  static getPaymentStatusName(status: number) {
    switch (status) {
      case PaymentStatusEnum.New:
      case PaymentStatusEnum.Pending:
      case PaymentStatusEnum.OnHold:
      case PaymentStatusEnum.ForApproval:
      case PaymentStatusEnum.Processed:
      case PaymentStatusEnum.Approved:
        return lang.commonOrderStatusPending();
      case PaymentStatusEnum.Completed:
        return lang.commonOrderStatusCompleted();
      case PaymentStatusEnum.Canceled:
      case PaymentStatusEnum.CanceledByClient:
        return lang.commonOrderStatusCanceled();
      case PaymentStatusEnum.Rejected:
        return lang.commonOrderStatusRejected();
      default:
        return '';
    }
  }

  static getPaymentProcessName(id: number) {
    switch (id) {
      case PaymentProcessEnum.Deposit:
        return lang.depositsHeader();
      case PaymentProcessEnum.Withdrawal:
        return lang.withdrawalsHeader();
      case PaymentProcessEnum.Transfer:
        return lang.fundScreenTransfer();
      case PaymentProcessEnum.Adjustment:
        return lang.fundScreenAdjustment();
      default:
        return '';
    }
  }

  static getFxOrderStatusNameLabel(status: FxOrderStatus) {
    switch (status) {
      case FxOrderStatus.New:
        return lang.commonOrderStatusPending();
      case FxOrderStatus.Fill:
        return lang.commonOrderStatusCompleted();
      case FxOrderStatus.Rejected:
        return lang.commonOrderStatusRejected();
      default:
        return '';
    }
  }

  static getFxOrderStatusName(fxOrder: FxOrder) {
    if (isSellEUR(fxOrder)) {
      return lang.walletSellBySymbol(CurrencyEnum.EUR);
    }

    return isBuyEUR(fxOrder) ? lang.walletBuyBySymbol(CurrencyEnum.EUR) : '';
  }

  static getAdjustmentCategoryName(id: string) {
    switch (id) {
      case AdjustmentCategoryEnum.ShortMarginInterest:
        return lang.fundScreenAdjustmentShortMargin();
      case AdjustmentCategoryEnum.OvernightBorrow:
        return lang.fundScreenAdjustmentOvernightBorrow();
      case AdjustmentCategoryEnum.StockLocate:
        return lang.fundScreenAdjustmentStockLocate();
      case AdjustmentCategoryEnum.MarketData:
        return lang.fundScreenAdjustmentMarketData();
      case AdjustmentCategoryEnum.Invoice:
        return lang.fundScreenAdjustmentInvoice();
      default:
        return id;
    }
  }

  static getPaymentMethodName(id: number) {
    switch (id) {
      case PaymentMethodEnum.BankTransfer:
        return lang.commonFirstDepositBank();
      case PaymentMethodEnum.Cash:
        return lang.cashHeader();
      case PaymentMethodEnum.eWallet:
        return lang.fundScreenEWallet();
      case PaymentMethodEnum.Card:
        return lang.fundScreenCardTransfer();
      case PaymentMethodEnum.InternalTransfer:
        return lang.fundScreenInternalTransfer();
      default:
        return '';
    }
  }

  static getTimeInForceName(timeInForce: string) {
    switch (timeInForce) {
      case TimeInForceEnum.EndOfDay:
        return lang.mobileTradingTicketDateOfExpireEndOfDayShort();
      case TimeInForceEnum.ExtendedHours:
        return lang.commonOrderTimeInForceExtendedHours();
      case TimeInForceEnum.GTD:
        return lang.mobileTradingTicketDateOfExpireGoodTillDate();
      case TimeInForceEnum.GTC:
        return lang.mobileTradingTicketDateOfExpireGoodTillCancelledShort();
      default:
        return '';
    }
  }

  static getOrderTypeName(orderType: NullableString|undefined, isLowerCase = false, isMobile = false): string {
    let result = '';

    switch (orderType) {
      case OrderTypeEnum.Limit:
        result = isMobile ? lang.mobileOrderCreateTabLimit() : lang.nonMobileOrderCreateTabLimit();
        break;
      case OrderTypeEnum.Market:
        result = lang.mobileOrderCreateTabMarket();
        break;
      case OrderTypeEnum.Pegged:
        result = lang.mobileOrderCreateTabPegged();
        break;
      case OrderTypeEnum.Stop:
        result = lang.mobileOrderCreateTabStop();
        break;
      case OrderTypeEnum.StopLimit:
        result = lang.mobileOrderCreateTabStopLimit();
        break;
      default:
        result = '';
    }

    return isLowerCase ? result.toLowerCase() : result;
  }

  static getAllowedFileExtensionNames(): string {
    return (
      AllowedFileExtensions
        .map((extension: string) => ` ${extension}`)
        .join(',')
    );
  }
}

export function trimObjectProperties(object: any) {
  if (!object) return object;

  let result: any = { };
  for (const key in object) {
    const element = object[key];
    if (isString(element)) {
      if (element.indexOf(PROPS_DELIMITER) > 0) {
        result[key] = compactPropsIntoString(parsePropsFromString(element));
      } else {
        result[key] = element.trim();
      }
    } else {
      result[key] = element;
    }
  }

  return result;
}

const PROPS_DELIMITER = '|';

export const parsePropsFromString = (value: NullableString, autoTrim = true) => {
  if (!value || !isString(value)) return [ '', '' ];

  let result = (
    value
      .split(PROPS_DELIMITER) ?? [ '', '' ]
      .map(item => item ?? '')
  );

  if (autoTrim) result = result.map(s => s.trim());

  return result;
};

export const compactPropsIntoString = (items: NullableString[], autoTrim = true) => {
  let result = items.map(item => item ?? '');
  if (autoTrim) result = result.map(item => item.trim());

  return result.join(PROPS_DELIMITER);
};

export const popularGroupTitleToName = (title: string) => title.replace(/ /gi, '-').toLowerCase();
